/* ============================================================================
   Fuentes y método. La vista que hace auditable todo lo demás.

   Si alguien va a tomar decisiones con estas cifras, tiene derecho a saber de
   dónde salió cada una, cuándo se consultó, qué se verificó y qué no. Esta vista
   se genera del propio dataset: no hay documentación paralela que se pueda quedar
   vieja.
   ========================================================================== */

import { Callout, Card, Chip, Section } from '../components/ui'
import { dataset } from '../data/dataset'
import { countAllPoints } from '../data/derive'
import { CATEGORY_LABEL, UNIT_LABEL, type DataNote } from '../data/schema'
import { formatDate, formatPeriodLong, formatPeriodTitle, integer, percent, pluralize } from '../lib/format'

const NOTE_KIND_LABEL: Record<DataNote['kind'], string> = {
  metodologia: 'Metodología',
  'corte-de-serie': 'Corte de serie',
  advertencia: 'Advertencia',
  contexto: 'Contexto',
}

const NOTE_KIND_TONE: Record<DataNote['kind'], 'info' | 'warning' | 'critical'> = {
  metodologia: 'info',
  'corte-de-serie': 'warning',
  advertencia: 'warning',
  contexto: 'info',
}

export function Fuentes() {
  const productSeriesCount = dataset.productSeries.length
  const placeSeriesCount = dataset.placeSeries.length
  const totalPoints = countAllPoints(dataset)
  /* Los tres límites de Profeco se declaran sólo si la fuente entró en esta
     corrida: dos de ellos citan cifras de `retail.contrasteInegi` y sin fuente
     no hay contraste que citar. Con `retail` en `null` los renglones no se
     imprimen y quien quiera saber por qué lo ve arriba, en el resultado de la
     ingesta de esa fuente, que sí se publica pase lo que pase. */
  const retail = dataset.retail

  return (
    <>
      <header className="view-head">
        <p className="t-micro view-head__eyebrow">Fuentes y método</p>
        <h1 className="t-title1">De dónde sale cada cifra</h1>
        <p className="t-body ink-2">
          Esta herramienta no produce estimaciones propias. Toma cifras publicadas por instituciones
          mexicanas, las normaliza y las presenta. Cada punto de cada serie guarda el identificador de
          su fuente y su nivel de confianza, y el pipeline se niega a escribir el archivo si algo no
          cuadra.
        </p>
      </header>

      <div className="grid grid--kpi">
        <Card>
          <p className="t-micro ink-3">Cobertura</p>
          <p className="t-title2">
            {formatPeriodTitle(dataset.coverage.firstPeriod)} — {formatPeriodTitle(dataset.coverage.lastPeriod)}
          </p>
          <p className="t-caption ink-3">
            {dataset.coverage.gaps.length === 0
              ? 'Serie mensual continua, sin huecos.'
              : `${dataset.coverage.gaps.length} ${pluralize(dataset.coverage.gaps.length, 'mes', 'meses')} sin dato.`}
          </p>
        </Card>
        <Card>
          <p className="t-micro ink-3">Puntos de dato</p>
          <p className="t-title2">{integer(totalPoints)}</p>
          <p className="t-caption ink-3">
            {dataset.series.length} series principales, {productSeriesCount} series por producto y{' '}
            {placeSeriesCount} series territoriales.
          </p>
        </Card>
        <Card>
          <p className="t-micro ink-3">Renglones con precio</p>
          <p className="t-title2">{dataset.products.length}</p>
          <p className="t-caption ink-3">
            Claves ENIGH distintas entre los dos ámbitos; la canasta rural y la urbana no tienen los
            mismos renglones. {Object.keys(CATEGORY_LABEL).length} categorías.
          </p>
        </Card>
        <Card>
          <p className="t-micro ink-3">Última ingesta</p>
          <p className="t-title2">{formatDate(dataset.ingestion.runAt)}</p>
          <p className="t-caption ink-3">
            {dataset.ingestion.results.filter((r) => r.status === 'ok' || r.status === 'sin-cambios').length} de{' '}
            {dataset.ingestion.results.length} fuentes sin incidencias.
          </p>
        </Card>
      </div>

      <Section
        title="Las canastas no son la misma cosa"
        description="En México coexisten varias definiciones de «canasta básica» y no son intercambiables. Estas son las que usa esta herramienta."
      >
        <div className="stack">
          {dataset.baskets.map((basket) => (
            <Card key={basket.id}>
              <div className="row row--between">
                <h3 className="t-title3">{basket.name}</h3>
                {basket.primary ? <Chip tone="accent">Indicador principal</Chip> : <Chip>Secundaria</Chip>}
              </div>
              <p className="t-footnote ink-2 basket__desc">{basket.description}</p>
              <dl className="deflist">
                <dt>Institución</dt>
                <dd>{basket.institution}</dd>
                <dt>Unidad</dt>
                <dd>{UNIT_LABEL[basket.unit]}</dd>
                {basket.productCount ? (
                  <>
                    <dt>Renglones</dt>
                    <dd>{basket.productCount}</dd>
                  </>
                ) : null}
              </dl>
              {basket.caveats?.length ? (
                <ul className="caveat-list">
                  {basket.caveats.map((caveat) => (
                    <li key={caveat} className="t-caption ink-2">
                      {caveat}
                    </li>
                  ))}
                </ul>
              ) : null}
            </Card>
          ))}
        </div>
      </Section>

      <Section
        title="Fuentes consultadas"
        description="Cada una con la URL exacta que consultó el pipeline y la fecha de consulta."
      >
        <div className="stack">
          {dataset.sources.map((source) => {
            const result = dataset.ingestion.results.find((r) => r.sourceId === source.id)
            return (
              <Card key={source.id}>
                <div className="row row--between">
                  <div className="stack stack--tight source__head">
                    <p className="t-micro ink-3">{source.institution}</p>
                    <h3 className="t-title3">{source.name}</h3>
                  </div>
                  <div className="row">
                    <Chip tone={source.verified ? 'accent' : 'neutral'}>
                      {source.verified ? 'Endpoint verificado' : 'Sin verificar'}
                    </Chip>
                    {result ? (
                      <Chip>
                        {result.status === 'ok'
                          ? 'Sin incidencias'
                          : result.status === 'error'
                            ? 'Con incidencias'
                            : result.status}
                      </Chip>
                    ) : null}
                  </div>
                </div>

                <dl className="deflist">
                  <dt>Cadencia</dt>
                  <dd>{source.cadence}</dd>
                  <dt>Consultada</dt>
                  <dd>{formatDate(source.retrievedAt)}</dd>
                  {source.license ? (
                    <>
                      <dt>Licencia</dt>
                      <dd>{source.license}</dd>
                    </>
                  ) : null}
                  <dt>URL</dt>
                  <dd className="source__url">
                    <a href={source.url} target="_blank" rel="noreferrer noopener">
                      {source.url}
                    </a>
                  </dd>
                  {source.methodologyUrl ? (
                    <>
                      <dt>Metodología</dt>
                      <dd className="source__url">
                        <a href={source.methodologyUrl} target="_blank" rel="noreferrer noopener">
                          {source.methodologyUrl}
                        </a>
                      </dd>
                    </>
                  ) : null}
                  {result ? (
                    <>
                      <dt>Resultado</dt>
                      <dd>{result.message}</dd>
                    </>
                  ) : null}
                </dl>

                {source.caveats?.length ? (
                  <ul className="caveat-list">
                    {source.caveats.map((caveat) => (
                      <li key={caveat} className="t-caption ink-2">
                        {caveat}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </Card>
            )
          })}
        </div>
      </Section>

      <Section
        title="Notas sobre los datos"
        description="Cortes de serie, advertencias e inconsistencias detectadas en la fuente. Nada de esto se corrige en silencio."
      >
        <div className="stack">
          {dataset.notes.map((note) => (
            <Callout key={note.id} kind={NOTE_KIND_TONE[note.kind]} title={NOTE_KIND_LABEL[note.kind]}>
              <p>
                <strong>{note.title}</strong>
              </p>
              <p>{note.body}</p>
              {note.sourceUrl ? (
                <p>
                  <a href={note.sourceUrl} target="_blank" rel="noreferrer noopener">
                    Ver la fuente
                  </a>
                </p>
              ) : null}
            </Callout>
          ))}
        </div>
      </Section>

      <Section
        title="Lo que esta herramienta todavía no hace"
        description="Declarado a propósito: un tablero que no dice sus límites invita a leerlo mal."
      >
        <Card>
          <ul className="gap-list">
            <li className="t-footnote ink-2">
              <strong>No hay canasta en pesos por estado ni por ciudad.</strong> La canasta oficial
              sólo se publica en dos ámbitos, rural y urbano, para todo el país. Territorio sí muestra
              cómo cambian los precios de los alimentos en las 32 entidades y en las 55 ciudades del
              Índice Nacional de Precios al Consumidor, pero eso es un índice: dice dónde suben más
              rápido, no dónde cuesta más comer. Convertirlo a pesos por ciudad exigiría una canasta
              local que nadie publica.
            </li>
            <li className="t-footnote ink-2">
              <strong>El directorio se publica completo; los contactos no.</strong> Nombre, razón
              social, domicilio, tamaño y coordenadas de cada establecimiento están aquí, uno por uno,
              y se pueden descargar. El correo y el teléfono no: el DENUE los trae para una parte de
              los establecimientos, y la mayoría de esos correos son de una persona, no de un buzón de
              empresa. Publicarlos aquí los convertiría en una lista raspable de un tirón. Esa lista
              existe y se genera en cada ingesta, pero vive fuera del sitio para que quien la use lo
              haga con su propio proceso de consentimiento.
            </li>
            <li className="t-footnote ink-2">
              <strong>El directorio no mide capacidad.</strong> El DENUE cuenta establecimientos y
              publica un estrato de empleados, no ventas ni volumen. Que una bodega tenga más de once
              personas la hace un candidato plausible para una compra grande, no un proveedor
              verificado.
            </li>
            <li className="t-footnote ink-2">
              <strong>Los precios promedio por ciudad quedaron fuera.</strong> El INEGI los publica en
              una consulta que arma la selección en la sesión del servidor y devuelve el archivo a
              partir de ella, no de los parámetros de la petición. Una ingesta mensual construida sobre
              eso se rompe en silencio, y el propio INEGI advierte que a nivel ciudad esa muestra es
              «únicamente informativa». Por eso Territorio usa el índice, que sí se publica como
              estadística representativa por ciudad.
            </li>
            <li className="t-footnote ink-2">
              <strong>No incluye la canasta del PACIC.</strong> Los 24 productos con precio objetivo de
              910 pesos que Profeco monitorea cada semana miden otra cosa —cumplimiento de un tope de
              precio en autoservicios, por hogar y por semana— y no son comparables con la canasta
              alimentaria. Mezclarlas produciría una cifra sin significado.
            </li>
            {retail !== null ? (
              <>
                <li className="t-footnote ink-2">
                  <strong>No convierte los precios de tienda en un costo de canasta.</strong>{' '}
                  Valuar la canasta completa con los precios de Profeco produciría una sexta cosa
                  llamada «canasta básica», y el sitio existe en parte para distinguir las cinco que
                  ya hay. Por eso <strong>Precio base</strong> da el precio de un producto concreto y
                  no suma renglones ni escala nada a un hogar: sobre los{' '}
                  {retail.contrasteInegi.renglones} renglones que sí se pueden contrastar contra el
                  boletín del INEGI de {formatPeriodLong(retail.contrasteInegi.period)}, la
                  diferencia mediana por renglón es de{' '}
                  {percent(retail.contrasteInegi.desviacionMedianaPct)} y sólo{' '}
                  {retail.contrasteInegi.dentroDe10} de {retail.contrasteInegi.renglones} caen dentro
                  de ±10 %. No es un error de ninguna de las dos: el INEGI mide el precio que paga un
                  hogar en todos los canales y Profeco mide el anaquel del autoservicio y el mayoreo
                  de la central. Son incombinables, no incompatibles.
                </li>
                <li className="t-footnote ink-2">
                  <strong>No cotiza: observa.</strong> El precio base sale de precios de venta en
                  establecimientos, no de cotizaciones de proveedores. Un mayorista que ofrezca por
                  debajo del precio base no está regalando nada: vende en volumen, sin tienda y sin
                  merma de anaquel. El precio base es el techo de la conversación, no el piso.
                </li>
                <li className="t-footnote ink-2">
                  <strong>
                    No sabe si las tiendas que visita Profeco representan a su ciudad.
                  </strong>{' '}
                  Se verificaron los metadatos y el diccionario de su portal de datos abiertos: no
                  existe una sola nota metodológica, ni tamaño de muestra, ni universo, ni criterio
                  de selección. Cuántos municipios cubre, qué entidades faltan por completo y cuánto
                  pesa el Valle de México sí están medidos, y se declaran arriba entre las
                  advertencias de esta fuente —no se repiten aquí porque una cifra escrita en dos
                  lugares se desincroniza en uno—. Mientras el diseño muestral no se publique, estas
                  cifras describen las tiendas que Profeco visitó, y ese es todo el universo que
                  pueden reclamar.
                </li>
              </>
            ) : null}
            <li className="t-footnote ink-2">
              <strong>El salario mínimo se mantiene a mano.</strong> Desde 2016 cambia una vez al año
              por acuerdo publicado en el Diario Oficial. Automatizar la descarga de un PDF anual con
              URL de CMS opaca sería más frágil que mantener un tramo por fijación con su cita; la
              ingesta se niega a escribir si la tabla se queda atrás del mes del dataset.
            </li>
            <li className="t-footnote ink-2">
              <strong>La serie por producto arranca en julio de 2025.</strong> Es el primer boletín
              mensual que el INEGI publicó con el desglose completo tras asumir la medición de pobreza.
              La serie del valor total de la canasta sí llega hasta enero de 1992.
            </li>
          </ul>
        </Card>
      </Section>
    </>
  )
}
